/**
 * Client-Side Image Resizer for QuattronDemo-v2
 * Preprocesses large images before server upload
 * Keeps existing server-side Sharp processing intact
 */

class ClientImageResizer {
  constructor() {
    this.maxWidth = 800; // Resize images wider than 800px
    this.maxHeight = 600; // Resize images taller than 600px
    this.quality = 0.85;  // 85% quality for compression
    this.maxFileSize = 2 * 1024 * 1024; // 2MB target after resize
    
    // Safe defaults for SSR - only check capabilities on client
    this.supportsWorkers = false;
    this.supportsOffscreenCanvas = false;
    
    // Only check capabilities on client side
    if (typeof window !== 'undefined') {
      this.supportsWorkers = 'Worker' in window;
      this.supportsOffscreenCanvas = 'OffscreenCanvas' in window;
    }
  }

  /**
   * Main resize function - determines if resize is needed
   * @param {File} file - The image file to potentially resize
   * @returns {Promise<File>} - The processed file (original if no resize needed)
   */
  async resizeIfNeeded(file) {
    try {
      // On server side (SSR), just return the original file
      if (typeof window === 'undefined') {
        console.log('SSR: Skipping client-side resize, returning original file');
        return file;
      }
      
      // Check if resize is needed
      const dimensions = await this.getImageDimensions(file);
      
      if (dimensions.width <= this.maxWidth && 
          dimensions.height <= this.maxHeight && 
          file.size <= this.maxFileSize) {
        console.log('Image within limits, no client-side resize needed');
        return file; // No resize needed
      }
      
      console.log(`Resizing image from ${dimensions.width}×${dimensions.height} to fit ${this.maxWidth}×${this.maxHeight}`);
      
      // Calculate new dimensions (maintain aspect ratio)
      const newDimensions = this.calculateNewDimensions(dimensions);
      
      // Resize using best available method
      if (this.supportsWorkers && this.supportsOffscreenCanvas) {
        return await this.resizeWithWorker(file, newDimensions);
      } else {
        return await this.resizeWithCanvas(file, newDimensions);
      }
      
    } catch (error) {
      console.warn('Client-side resize failed, using original:', error);
      return file; // Fallback to original file
    }
  }

  /**
   * Get image dimensions without loading full image
   * @param {File} file - The image file
   * @returns {Promise<{width: number, height: number}>} - Image dimensions
   */
  async getImageDimensions(file) {
    // Return early if running on server (SSR)
    if (typeof window === 'undefined') {
      throw new Error('Cannot get image dimensions on server side');
    }
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image dimensions'));
      };
      
      img.src = url;
    });
  }

  /**
   * Calculate new dimensions maintaining aspect ratio
   * @param {Object} current - Current dimensions {width, height}
   * @returns {Object} - New dimensions {width, height}
   */
  calculateNewDimensions(current) {
    const { width: currentWidth, height: currentHeight } = current;
    
    // Calculate scale factor
    const scaleX = this.maxWidth / currentWidth;
    const scaleY = this.maxHeight / currentHeight;
    const scale = Math.min(scaleX, scaleY, 1); // Don't upscale
    
    return {
      width: Math.round(currentWidth * scale),
      height: Math.round(currentHeight * scale)
    };
  }

  /**
   * Resize using Web Worker + OffscreenCanvas (Best Performance)
   * @param {File} file - The image file
   * @param {Object} dimensions - Target dimensions {width, height}
   * @returns {Promise<Blob>} - Resized image blob
   */
  async resizeWithWorker(file, dimensions) {
    // Ensure we're running on client side
    if (typeof window === 'undefined' || !this.supportsWorkers || !this.supportsOffscreenCanvas) {
      throw new Error('Worker-based resize not available on server side or unsupported environment');
    }
    
    const bitmap = await createImageBitmap(file);
    
    return new Promise((resolve, reject) => {
      const worker = new Worker('/workers/image-resizer-worker.js');
      
      const timeoutId = setTimeout(() => {
        worker.terminate();
        reject(new Error('Resize timeout'));
      }, 15000); // 15 second timeout
      
      worker.onmessage = (e) => {
        clearTimeout(timeoutId);
        worker.terminate();
        
        if (e.data.error) {
          reject(new Error(e.data.error));
        } else {
          // Create File object from blob with original file properties
          const resizedFile = new File([e.data.blob], file.name, {
            type: e.data.blob.type,
            lastModified: Date.now()
          });
          resolve(resizedFile);
        }
      };
      
      worker.onerror = (error) => {
        clearTimeout(timeoutId);
        worker.terminate();
        reject(error);
      };
      
      // Transfer bitmap to worker (zero-copy in Chrome)
      worker.postMessage({
        bitmap,
        width: dimensions.width,
        height: dimensions.height,
        quality: this.quality
      }, [bitmap]);
    });
  }

  /**
   * Resize using main thread Canvas (Fallback)
   * @param {File} file - The image file
   * @param {Object} dimensions - Target dimensions {width, height}
   * @returns {Promise<File>} - Resized image file
   */
  async resizeWithCanvas(file, dimensions) {
    // Ensure we're running on client side
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('Canvas-based resize not available on server side');
    }
    
    const bitmap = await createImageBitmap(file);
    
    const canvas = document.createElement('canvas');
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Draw resized image
    ctx.drawImage(bitmap, 0, 0, dimensions.width, dimensions.height);
    
    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        // Create File object from blob with original file properties
        const resizedFile = new File([blob], file.name, {
          type: blob.type,
          lastModified: Date.now()
        });
        resolve(resizedFile);
      }, 'image/webp', this.quality);
    });
  }
}

// Export singleton instance
export default new ClientImageResizer();